import React, { useEffect, useState } from 'react';
import { getTournamentTeamRanking, TeamStatistics } from '../../services/statisticsService';
import { formatDistance, formatTime, formatDateFromArray } from '../../utils/formatters';
import { FaUsers, FaTrophy, FaSpinner, FaExclamationTriangle, FaRunning, FaRoad, FaClock, FaUserFriends, FaMedal, FaCrown } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';

interface TournamentTeamRankingProps {
  tournamentId: number;
  startDateLocal: string;
  endDate: string;
}

const TournamentTeamRanking: React.FC<TournamentTeamRankingProps> = ({ tournamentId, startDateLocal, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [teamRanking, setTeamRanking] = useState<TeamStatistics[]>([]);

  useEffect(() => {
    const fetchTeamRanking = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getTournamentTeamRanking(tournamentId, startDateLocal, endDate);
        setTeamRanking(data);
      } catch (err: any) {
        setError(err.message || 'Có lỗi xảy ra khi lấy bảng xếp hạng đội trong giải đấu');
      } finally {
        setLoading(false);
      }
    };

    if (tournamentId) {
      fetchTeamRanking();
    }
  }, [tournamentId, startDateLocal, endDate]);

  // Hàm render cho bục xếp hạng
  const renderTopTeamsPodium = (teams: TeamStatistics[]) => {
    if (teams.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '20px' }}>
          <div className="empty-state-text">Không có dữ liệu xếp hạng</div>
        </div>
      );
    }

    // Lấy top 3 đội hoặc ít hơn nếu không đủ 3 đội
    const topTeams = teams.slice(0, Math.min(3, teams.length));

    return (
      <div className="top3-users">
        {topTeams.map((team, index) => (
          <div key={team.teamId} className={`top-user-podium top-${index + 1}`}>
            <div className="medal-icon">
              {index === 0 && <FaMedal color="#FFD700" size={24} />}
              {index === 1 && <FaMedal color="#C0C0C0" size={22} />}
              {index === 2 && <FaMedal color="#CD7F32" size={20} />}
            </div>
            
            <div className="team-avatar-placeholder" style={{ backgroundColor: getTeamColor(index) }}>
              {team.teamName.charAt(0).toUpperCase()}
            </div>
            
            <div className="top-user-name">{team.teamName}</div>
            <div className="top-user-distance">
              <FaRoad size={12} style={{ marginRight: 5 }} /> 
              {formatDistance(team.totalDistance)}
            </div>
            <div className="top-user-activities">
              <FaRunning size={12} style={{ marginRight: 5 }} />
              {team.activityCount} hoạt động
            </div>
            {team.topUser && (
              <div className="team-top-member">
                <FaUserFriends size={12} style={{ marginRight: 5 }} />
                {team.userCount} thành viên
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Hàm lấy màu cho các đội
  const getTeamColor = (index: number) => {
    const colors = ['#20757A', '#C1322D', '#6A5ACD', '#2E8B57', '#FF8C00', '#8B4513'];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <FaExclamationTriangle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  if (!teamRanking || teamRanking.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <FaUsers />
        </div>
        <div className="empty-state-text">Không có dữ liệu xếp hạng đội cho giải đấu này</div>
      </div>
    );
  }

  // Sắp xếp theo quãng đường giảm dần
  const sortedRanking = [...teamRanking].sort((a, b) => b.totalDistance - a.totalDistance);

  return (
    <div className={`tournament-team-ranking ${theme}`}>
      {/* Bục xếp hạng cho top 3 đội */}
      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <h2 className="statistics-card-title">
            <FaCrown /> Top đội xuất sắc
          </h2>
        </div>
        
        {renderTopTeamsPodium(sortedRanking)}
      </div>
      
      {/* Bảng xếp hạng tất cả các đội */}
      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <h2 className="statistics-card-title">
            <FaTrophy /> Bảng xếp hạng đội
          </h2>
        </div>
        
        <table className="ranking-table">
          <thead>
            <tr>
              <th>Thứ hạng</th>
              <th>Tên đội</th>
              <th>Quãng đường</th>
              <th>Số thành viên</th>
              <th>Hoạt động</th>
              <th>Thời gian</th>
            </tr>
          </thead>
          <tbody>
            {sortedRanking.map((team, index) => (
              <tr key={team.teamId} className={index < 3 ? `top-${index + 1}-row` : ''}>
                <td className="rank-cell">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', marginRight: '5px' }}>
                      {index === 0 && <FaMedal color="#FFD700" size={16} />}
                      {index === 1 && <FaMedal color="#C0C0C0" size={16} />}
                      {index === 2 && <FaMedal color="#CD7F32" size={16} />}
                    </span>
                    <span>#{index + 1}</span>
                  </div>
                </td>
                <td>
                  <div className="team-info">
                    <div 
                      className="team-avatar-circle" 
                      style={{ 
                        backgroundColor: getTeamColor(index),
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        marginRight: '10px'
                      }}
                    >
                      {team.teamName.charAt(0).toUpperCase()}
                    </div>
                    <span className="team-name">{team.teamName}</span>
                  </div>
                </td>
                <td className="distance-cell">{formatDistance(team.totalDistance)}</td>
                <td>{team.userCount}</td>
                <td className="activity-count-cell">{team.activityCount}</td>
                <td>{formatTime(team.totalMovingTime)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Thông tin chi tiết về các đội */}
      {sortedRanking.length > 0 && (
        <div className={`statistics-card ${theme}`}>
          <div className="statistics-card-header">
            <h2 className="statistics-card-title">
              <FaUsers /> Thông tin chi tiết đội
            </h2>
          </div>
          
          <div className="team-details-container">
            {sortedRanking.map((team, index) => (
              <div 
                key={team.teamId} 
                className={`team-detail-card ${index < 3 ? `team-rank-${index + 1}` : ''}`}
                style={{
                  padding: '20px',
                  marginBottom: '20px',
                  backgroundColor: theme === 'dark' ? '#3a3e40' : 'white',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  border: index < 3 ? `2px solid ${index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'}` : 'none'
                }}
              >
                <div className="team-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>

                  
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
                        {index === 0 && <FaMedal color="#FFD700" size={20} />}
                        {index === 1 && <FaMedal color="#C0C0C0" size={20} />}
                        {index === 2 && <FaMedal color="#CD7F32" size={20} />}
                      </span>
                      <span>{team.teamName}</span>
                    </h3>
                    <div style={{ fontSize: '0.9rem', color: theme === 'dark' ? '#bbb' : '#666' }}>
                      Hạng #{index + 1} - Tham gia {team.tournamentName}
                    </div>
                  </div>
                </div>
                
                <div className="team-stats-grid" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '15px',
                  marginBottom: '20px'
                }}>
                  <div className="team-stat-item" style={{ 
                    backgroundColor: theme === 'dark' ? 'rgba(32, 117, 122, 0.1)' : 'rgba(32, 117, 122, 0.05)',
                    padding: '15px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#20757A', display: 'flex', alignItems: 'center' }}>
                      <FaRoad style={{ marginRight: '8px' }} />
                      {formatDistance(team.totalDistance)}
                    </div>
                    <div style={{ color: theme === 'dark' ? '#bbb' : '#666', fontSize: '0.9rem' }}>Tổng quãng đường</div>
                  </div>
                  
                  <div className="team-stat-item" style={{ 
                    backgroundColor: theme === 'dark' ? 'rgba(193, 50, 45, 0.1)' : 'rgba(193, 50, 45, 0.05)',
                    padding: '15px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#C1322D', display: 'flex', alignItems: 'center' }}>
                      <FaUserFriends style={{ marginRight: '8px' }} />
                      {team.userCount}
                    </div>
                    <div style={{ color: theme === 'dark' ? '#bbb' : '#666', fontSize: '0.9rem' }}>Thành viên</div>
                  </div>
                  
                  <div className="team-stat-item" style={{ 
                    backgroundColor: theme === 'dark' ? 'rgba(106, 90, 205, 0.1)' : 'rgba(106, 90, 205, 0.05)',
                    padding: '15px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#6A5ACD', display: 'flex', alignItems: 'center' }}>
                      <FaRunning style={{ marginRight: '8px' }} />
                      {team.activityCount}
                    </div>
                    <div style={{ color: theme === 'dark' ? '#bbb' : '#666', fontSize: '0.9rem' }}>Hoạt động</div>
                  </div>
                  
                  <div className="team-stat-item" style={{ 
                    backgroundColor: theme === 'dark' ? 'rgba(46, 139, 87, 0.1)' : 'rgba(46, 139, 87, 0.05)',
                    padding: '15px',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2E8B57', display: 'flex', alignItems: 'center' }}>
                      <FaClock style={{ marginRight: '8px' }} />
                      {formatTime(team.totalMovingTime)}
                    </div>
                    <div style={{ color: theme === 'dark' ? '#bbb' : '#666', fontSize: '0.9rem' }}>Thời gian chạy</div>
                  </div>
                </div>
                
                {team.topUser && (
                  <div className="team-top-user" style={{ 
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: theme === 'dark' ? 'rgba(255, 140, 0, 0.1)' : 'rgba(255, 140, 0, 0.05)',
                    borderRadius: '8px'
                  }}>
                    <div style={{ 
                      fontWeight: 'bold', 
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#FF8C00'
                    }}>
                      <FaMedal style={{ marginRight: '8px', color: '#FF8C00' }} />
                      Thành viên xuất sắc nhất
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {team.topUser.avatarUrl ? (
                        <img 
                          src={team.topUser.avatarUrl} 
                          alt={team.topUser.fullName}
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '50%', 
                            marginRight: '10px',
                            border: '2px solid #FF8C00'
                          }}
                        />
                      ) : (
                        <div style={{ 
                          width: '40px', 
                          height: '40px', 
                          borderRadius: '50%', 
                          backgroundColor: '#FF8C00',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          marginRight: '10px'
                        }}>
                          {team.topUser.fullName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{team.topUser.fullName}</div>
                        <div style={{ 
                          display: 'flex', 
                          fontSize: '0.9rem',
                          color: theme === 'dark' ? '#bbb' : '#666'
                        }}>
                          <span style={{ 
                            marginRight: '15px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <FaRoad size={12} style={{ marginRight: '5px' }} /> 
                            {formatDistance(team.topUser.totalDistance)}
                          </span>
                          <span style={{ 
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <FaRunning size={12} style={{ marginRight: '5px' }} /> 
                            {team.topUser.activityCount} hoạt động
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentTeamRanking;